// Feature Flags page — runtime toggles the app respects at request time.
//
// Currently just the LLM provider order (Google / Groq / Cerebras). Drag
// to reorder — index 0 is the active provider, the rest are fallbacks in
// order. The backend calls providers in this order and falls forward on
// quota (429) or any other error. Changes take effect within ~60s (the
// llm module's in-memory cache TTL); the backend also invalidates the
// cache on save so the next request always sees the new order.
//
// Add more flag sections here as they land (e.g. maintenance mode,
// experimental features) — just fetch/save via /api/admin/settings/<key>.

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Snackbar, Alert, Chip } from '@mui/material';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import api from '../config/api';

const ff = "'Poppins', sans-serif";

function ProviderRow({ id, provider, index }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const isActive = index === 0;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        mb: 1.5,
        borderRadius: '12px',
        border: `1px solid ${isActive ? '#32A6D8' : '#E5E7EB'}`,
        background: isActive ? '#F0F9FF' : '#FFF',
        cursor: 'grab',
      }}
      {...attributes}
      {...listeners}
    >
      <GripVertical size={18} color="#9CA3AF" />
      <Box sx={{
        width: 32, height: 32, borderRadius: '50%',
        background: isActive ? '#32A6D8' : '#E5E7EB',
        color: isActive ? '#FFF' : '#4A5568',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: ff, fontWeight: 700, fontSize: 14,
      }}>
        {index + 1}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ fontFamily: ff, fontSize: 14, fontWeight: 600, color: '#1A202C' }}>
            {provider.displayName}
          </Box>
          {isActive && (
            <Chip label="ACTIVE" size="small" sx={{
              fontFamily: ff, fontSize: 10, fontWeight: 700, height: 20,
              background: '#32A6D8', color: '#FFF',
            }} />
          )}
        </Box>
        <Box sx={{ fontFamily: ff, fontSize: 12, color: '#6B7280', mt: 0.25 }}>
          {isActive ? 'Primary provider — tried first' : `Fallback #${index}`}
        </Box>
      </Box>
      {provider.configured ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#059669' }}>
          <CheckCircle2 size={14} />
          <Box sx={{ fontFamily: ff, fontSize: 11, fontWeight: 600 }}>Configured</Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#DC2626' }}>
          <XCircle size={14} />
          <Box sx={{ fontFamily: ff, fontSize: 11, fontWeight: 600 }}>No API key</Box>
        </Box>
      )}
    </Box>
  );
}

export default function FeatureFlags() {
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState([]); // string[] of provider names
  const [providers, setProviders] = useState({}); // { name: { displayName, configured } }
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/admin/settings/llm');
        const data = res.data?.data;
        if (data) {
          setOrder(data.order || []);
          const map = {};
          (data.providers || []).forEach((p) => { map[p.name] = p; });
          setProviders(map);
        }
      } catch (err) {
        if (!err.silent) setSnack({ open: true, message: 'Failed to load provider settings', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrder((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      const next = arrayMove(prev, oldIndex, newIndex);
      save(next);
      return next;
    });
  };

  const save = async (nextOrder) => {
    setSaving(true);
    try {
      await api.put('/admin/settings/llm', { order: nextOrder });
      setSnack({ open: true, message: `Active provider: ${providers[nextOrder[0]]?.displayName || nextOrder[0]}`, severity: 'success' });
    } catch (err) {
      if (!err.silent) setSnack({ open: true, message: err.response?.data?.message || 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress sx={{ color: '#32A6D8' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{
        background: '#FFF', borderRadius: '16px', p: 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
        maxWidth: 720, mx: 'auto',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Sparkles size={22} color="#32A6D8" />
          <Box sx={{ fontFamily: ff, fontSize: 20, fontWeight: 700, color: '#1A202C' }}>
            AI text generation
          </Box>
          {saving && <CircularProgress size={16} sx={{ color: '#32A6D8', ml: 'auto' }} />}
        </Box>
        <Box sx={{ fontFamily: ff, fontSize: 13, color: '#6B7280', lineHeight: 1.6, mb: 3 }}>
          The app calls providers in this order for AI-generated text (pet descriptions, sitter About sections). If one hits a quota limit or errors, the next one is tried automatically. Drag to reorder — the top provider is active.
        </Box>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            {order.map((name, i) => (
              <ProviderRow
                key={name}
                id={name}
                index={i}
                provider={providers[name] || { name, displayName: name, configured: false }}
              />
            ))}
          </SortableContext>
        </DndContext>

        <Box sx={{
          mt: 2, p: 2, borderRadius: '12px',
          background: '#FFFBEB', border: '1px solid #FEF3C7',
          fontFamily: ff, fontSize: 12, color: '#92400E', lineHeight: 1.5,
        }}>
          <strong>Note:</strong> A provider marked "No API key" will be skipped at runtime. Set the corresponding env var on the server (GEMINI_API_KEY, GROQ_API_KEY, CEREBRAS_API_KEY) and redeploy to activate it.
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} sx={{ fontFamily: ff }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
